import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useTerminologia } from '../hooks/useTerminologia';

const HOST_API = process.env.REACT_APP_URL;

const TASA_EMPTY = {
    tasaMunicipalUFId: 0,
    unidadFuncionalId: null,
    partidaArba:       '',
    periodo:            null,
    importe:            null,
    descuentos:         null,
    total:              null,
};

const fmtFecha    = (v) => v ? new Date(v).toLocaleDateString('es-AR') : '—';
const fmtImporte  = (v) => v != null ? `$ ${Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : '—';
const fmtPeriodo  = (v) => v
    ? new Date(v).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })
    : '—';

const TasasMunicipales = () => {
    const toast = useRef(null);
    const fileInputRef = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const t = useTerminologia();

    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');

    // ─── Data ────────────────────────────────────────────────────────────────────
    const [tasas,   setTasas]   = useState([]);
    const [ufs,     setUfs]     = useState([]);
    const [loading, setLoading] = useState(false);

    // ─── Filtros ─────────────────────────────────────────────────────────────────
    const [filtroPeriodo, setFiltroPeriodo] = useState(null);

    // ─── Modal editar ────────────────────────────────────────────────────────────
    const [modalTasa,  setModalTasa]  = useState(false);
    const [form,        setForm]       = useState({ ...TASA_EMPTY });
    const [submitting,  setSubmitting] = useState(false);

    // ─── Modal importar Excel ─────────────────────────────────────────────────────
    const [modalExcel,   setModalExcel]   = useState(false);
    const [excelFile,    setExcelFile]    = useState(null);
    const [excelPeriodo, setExcelPeriodo] = useState(null);
    const [importing,    setImporting]    = useState(false);

    // ─── Carga UFs ───────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setUfs(data))
            .catch(() => {});
    }, [barrio?.id]);

    // ─── Carga tasas ─────────────────────────────────────────────────────────────
    const fetchTasas = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            let url = `${HOST_API}api/tasasMunicipales/GetTasas?administracionId=${administracionId}&barrioId=${barrio.id}`;
            if (filtroPeriodo) url += `&periodo=${filtroPeriodo.toISOString()}`;
            const { data } = await axios.get(url);
            setTasas(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las tasas municipales', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, filtroPeriodo, administracionId]);

    useEffect(() => { fetchTasas(); }, [fetchTasas]);

    // ─── Editar tasa ─────────────────────────────────────────────────────────────
    const editarTasa = (row) => {
        setForm({
            tasaMunicipalUFId: row.tasaMunicipalUFId,
            unidadFuncionalId: row.unidadFuncionalId,
            partidaArba:       row.partidaArba || '',
            periodo:           row.periodo ? new Date(row.periodo) : null,
            importe:           row.importe,
            descuentos:        row.descuentos,
            total:             row.total,
        });
        setModalTasa(true);
    };

    const guardarTasa = async () => {
        if (!form.unidadFuncionalId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: `Seleccione la ${t.UnidadFuncional}`, life: 3000 }); return; }
        if (!form.partidaArba)       { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese la partida ARBA', life: 3000 }); return; }
        if (!form.periodo)           { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el período', life: 3000 }); return; }
        if (form.importe == null)    { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el importe', life: 3000 }); return; }
        if (form.total == null)      { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el total', life: 3000 }); return; }

        setSubmitting(true);
        try {
            const payload = {
                tasaMunicipalUFId: form.tasaMunicipalUFId,
                barrioId:          barrio.id,
                administracionId,
                unidadFuncionalId: form.unidadFuncionalId,
                partidaArba:       form.partidaArba,
                importe:           form.importe,
                descuentos:        form.descuentos,
                total:             form.total,
                periodo:           form.periodo,
            };
            await axios.post(`${HOST_API}api/tasasMunicipales/SaveTasa`, payload);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Tasa municipal guardada correctamente', life: 3000 });
            setModalTasa(false);
            fetchTasas();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo guardar la tasa municipal', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Eliminar tasa ───────────────────────────────────────────────────────────
    const eliminarTasa = (row) => {
        confirmDialog({
            message:      `¿Eliminar la tasa municipal de la partida ${row.partidaArba} (${t.UF} ${row.unidadFuncional})?`,
            header:       'Confirmar eliminación',
            icon:         'pi pi-exclamation-triangle',
            acceptLabel:  'Eliminar',
            rejectLabel:  'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/tasasMunicipales/DeleteTasa?tasaMunicipalUFId=${row.tasaMunicipalUFId}&administracionId=${administracionId}`);
                    toast.current.show({ severity: 'success', summary: 'Eliminada', detail: 'Tasa municipal eliminada', life: 3000 });
                    fetchTasas();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar la tasa municipal', life: 3000 });
                }
            },
        });
    };

    // ─── Importar Excel ───────────────────────────────────────────────────────────
    const handleExcelChange = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        const ext = f.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls'].includes(ext)) {
            toast.current.show({ severity: 'warn', summary: 'Formato inválido', detail: 'Solo se aceptan archivos .xlsx o .xls', life: 3000 });
            return;
        }
        setExcelFile(f);
    };

    const importarExcel = async () => {
        if (!excelFile)    { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione un archivo Excel', life: 3000 }); return; }
        if (!excelPeriodo) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el período', life: 3000 }); return; }

        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('file',             excelFile);
            fd.append('administracionId', administracionId);
            fd.append('periodo',          excelPeriodo.toISOString());

            const { data } = await axios.post(`${HOST_API}api/tasasMunicipales/ImportarExcel`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.current.show({
                severity: 'success',
                summary:  'Importación finalizada',
                detail:   `${data.importados} importadas${data.noEncontrados ? `, ${data.noEncontrados} sin UF encontrada` : ''}`,
                life:     4500,
            });
            setModalExcel(false);
            setExcelFile(null);
            setExcelPeriodo(null);
            fetchTasas();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'Error al importar el archivo', life: 4000 });
        } finally {
            setImporting(false);
        }
    };

    const descargarPlantilla = () => {
        const csv = 'fecha;partidaArba;importe;descuentos;total;Imputacion\n03/06/2026;8888888;16101.55;;16101.55;1\n03/06/2026;9999999;16101.55;;16101.55;2\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = 'plantilla_tasas_municipales.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    // ─── Templates de columna ─────────────────────────────────────────────────────
    const importeTemplate    = (row) => fmtImporte(row.importe);
    const descuentosTemplate = (row) => fmtImporte(row.descuentos);
    const totalTemplate      = (row) => fmtImporte(row.total);
    const periodoTemplate    = (row) => fmtPeriodo(row.periodo);
    const accionesTemplate   = (row) => (
        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm"
                tooltip="Editar" tooltipOptions={{ position: 'top' }}
                onClick={() => editarTasa(row)} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                tooltip="Eliminar" tooltipOptions={{ position: 'top' }}
                onClick={() => eliminarTasa(row)} />
        </div>
    );

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            <h4>Tasas Municipales</h4>
            <br />
            <Toast ref={toast} />
            <ConfirmDialog />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            {/* ─── Barra superior ──────────────────────────────────────────────── */}
            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="flex gap-2 flex-wrap align-items-end">
                    <div>
                        <label style={lbl}>Período</label>
                        <Calendar value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.value)}
                            view="month" dateFormat="mm/yy" placeholder="Todos" showButtonBar
                            className="p-inputtext-sm" style={{ width: 140 }} />
                    </div>
                    <Button label="Buscar" icon="pi pi-search" onClick={fetchTasas} className="p-button-sm" />
                </div>
                <div className="flex gap-2">
                    <Button label="Importar Excel" icon="pi pi-file-excel" className="p-button-sm p-button-success"
                        onClick={() => { setModalExcel(true); setExcelFile(null); setExcelPeriodo(null); }} />
                </div>
            </div>

            {/* ─── Grilla ──────────────────────────────────────────────────────── */}
            <div className="grid">
                <div className="col-12">
                    <DataTable value={tasas} paginator rows={15} rowsPerPageOptions={[10, 15, 25, 50]}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay tasas municipales registradas"
                        className="p-datatable-gridlines bg-white"
                        sortField="periodo" sortOrder={-1}>
                        <Column field="unidadFuncional"  header={t.UF}          sortable style={{ width: '6rem', textAlign: 'center' }} />
                        <Column field="partidaArba"      header="Partida ARBA" sortable style={{ width: '10rem' }} />
                        <Column field="periodo"          header="Período"      sortable body={periodoTemplate} style={{ width: '9rem' }} />
                        <Column field="importe"          header="Importe"      sortable body={importeTemplate}    style={{ width: '9rem', textAlign: 'right' }} />
                        <Column field="descuentos"       header="Descuentos"   sortable body={descuentosTemplate} style={{ width: '9rem', textAlign: 'right' }} />
                        <Column field="total"            header="Total"        sortable body={totalTemplate}      style={{ width: '9rem', textAlign: 'right' }} />
                        <Column header="Acciones" body={accionesTemplate} style={{ width: '7rem', textAlign: 'center' }} />
                    </DataTable>
                </div>
            </div>

            {/* ─── Modal: Editar Tasa ───────────────────────────────────────────── */}
            <Dialog header="Editar Tasa Municipal" visible={modalTasa} style={{ width: '480px' }} modal
                onHide={() => setModalTasa(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalTasa(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardarTasa} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-12">
                        <label style={lbl}>{t.UnidadFuncional} *</label>
                        <Dropdown value={form.unidadFuncionalId} options={ufs} optionLabel="unidadFuncional" optionValue="unidadFuncionalId"
                            onChange={(e) => setForm(f => ({ ...f, unidadFuncionalId: e.value }))}
                            placeholder={`Seleccionar ${t.UF}`} filter className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Partida ARBA *</label>
                        <InputText value={form.partidaArba} onChange={(e) => setForm(f => ({ ...f, partidaArba: e.target.value }))}
                            className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Período *</label>
                        <Calendar value={form.periodo} onChange={(e) => setForm(f => ({ ...f, periodo: e.value }))}
                            view="month" dateFormat="mm/yy" placeholder="mm/aaaa" className="p-inputtext-sm" />
                    </div>
                    <div className="col-4">
                        <label style={lbl}>Importe *</label>
                        <InputNumber value={form.importe} onValueChange={(e) => setForm(f => ({ ...f, importe: e.value }))}
                            mode="currency" currency="ARS" locale="es-AR" className="p-inputtext-sm" />
                    </div>
                    <div className="col-4">
                        <label style={lbl}>Descuentos</label>
                        <InputNumber value={form.descuentos} onValueChange={(e) => setForm(f => ({ ...f, descuentos: e.value }))}
                            mode="currency" currency="ARS" locale="es-AR" className="p-inputtext-sm" />
                    </div>
                    <div className="col-4">
                        <label style={lbl}>Total *</label>
                        <InputNumber value={form.total} onValueChange={(e) => setForm(f => ({ ...f, total: e.value }))}
                            mode="currency" currency="ARS" locale="es-AR" className="p-inputtext-sm" />
                    </div>
                </div>
            </Dialog>

            {/* ─── Modal: Importar Excel ────────────────────────────────────────── */}
            <Dialog header="Importar Tasas Municipales desde Excel" visible={modalExcel} style={{ width: '460px' }} modal
                onHide={() => setModalExcel(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalExcel(false)} disabled={importing} />
                        <Button label="Importar" icon="pi pi-upload" className="p-button-sm p-button-success" onClick={importarExcel} loading={importing} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    {/* Formato esperado */}
                    <div className="col-12">
                        <div style={{ background: '#f8f9fa', border: '1px solid #e4e7ec', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#344054' }}>
                            <strong>Formato del archivo:</strong><br />
                            <span style={{ color: '#667085' }}>Columnas (fila 1 = títulos):</span>
                            <ol style={{ margin: '0.35rem 0 0 1rem', padding: 0, lineHeight: 1.8 }}>
                                <li><b>A:</b> Fecha</li>
                                <li><b>B:</b> Partida ARBA</li>
                                <li><b>C:</b> Importe</li>
                                <li><b>D:</b> Descuentos (opcional)</li>
                                <li><b>E:</b> Total</li>
                                <li><b>F:</b> Imputación ({t.Barrio})</li>
                            </ol>
                        </div>
                        <Button label="Descargar plantilla" icon="pi pi-download" className="p-button-text p-button-sm mt-2"
                            onClick={descargarPlantilla} />
                    </div>

                    <div className="col-12">
                        <label style={lbl}>Período *</label>
                        <Calendar value={excelPeriodo} onChange={(e) => setExcelPeriodo(e.value)}
                            view="month" dateFormat="mm/yy" placeholder="mm/aaaa" className="p-inputtext-sm" />
                    </div>

                    <div className="col-12">
                        <label style={lbl}>Archivo Excel (.xlsx / .xls) *</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                                onChange={handleExcelChange} />
                            <Button label={excelFile ? excelFile.name : 'Seleccionar archivo'}
                                icon="pi pi-file-excel"
                                className={`p-button-outlined p-button-sm ${excelFile ? 'p-button-success' : ''}`}
                                style={{ flex: 1, justifyContent: 'flex-start', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                onClick={() => fileInputRef.current.click()} />
                            {excelFile && (
                                <Button icon="pi pi-times" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                    onClick={() => { setExcelFile(null); fileInputRef.current.value = ''; }} />
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default TasasMunicipales;
